// Adds emoji and link actions to the message editor.

import { useEffect, useMemo, useState } from 'react';
import { Link2, Search, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { useMessageEditor } from './use-message-editor';

type Editor = ReturnType<typeof useMessageEditor>;

interface EmojiGroup {
  name: string;
  slug: string;
  emojis: { emoji: string; name: string; slug: string }[];
}

export function EmojiButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [emojiGroups, setEmojiGroups] = useState<EmojiGroup[] | null>(null);

  useEffect(() => {
    if (!open || emojiGroups) return;
    let active = true;
    void import('./emoji-data.json').then(({ default: groups }) => {
      if (active) setEmojiGroups(groups as EmojiGroup[]);
    });
    return () => {
      active = false;
    };
  }, [emojiGroups, open]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!emojiGroups || !term) return emojiGroups ?? [];
    return emojiGroups.map((group) => ({
      ...group,
      emojis: group.emojis.filter((item) => item.slug.includes(term) || item.name.includes(term)),
    })).filter((group) => group.emojis.length > 0);
  }, [emojiGroups, search]);

  const insert = (emoji: string) => {
    editor.insertText(emoji);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-6 text-gray-900 hover:bg-gray-100 hover:text-text-heading"
          aria-label="Emoji"
        >
          <Smile className="size-[14px]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-[312px] rounded-xl p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-700" />
          <Input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search emoji"
            className="h-8 rounded-lg pl-8 text-[13px]"
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto pr-1">
          {emojiGroups === null && <p className="py-6 text-center text-[12px] text-gray-700">Loading emoji…</p>}
          {emojiGroups !== null && groups.length === 0 && (
            <p className="py-6 text-center text-[12px] text-gray-700">No emoji found.</p>
          )}
          {groups.map((group) => (
            <div key={group.slug} className="mb-2 last:mb-0">
              <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-gray-700">{group.name}</p>
              <div className="grid grid-cols-8 gap-0.5">
                {group.emojis.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => insert(item.emoji)}
                    title={item.name}
                    className="flex size-7 cursor-pointer items-center justify-center rounded-md text-[16px] leading-none transition-colors hover:bg-gray-100"
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LinkButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState('');

  const apply = () => {
    const url = href.trim();
    if (!url) return;
    editor.setLink(url);
    setHref('');
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setHref('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-6 text-gray-900 hover:bg-gray-100 hover:text-text-heading"
          aria-label="Link"
        >
          <Link2 className="size-[14px]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-[260px] rounded-xl p-2">
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                apply();
              }
            }}
            placeholder="Paste a link"
            className="h-8 rounded-lg text-[13px]"
          />
          <Button size="sm" onClick={apply} disabled={!href.trim()} className="text-[13px] font-normal">
            Add
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
